import { useState } from "react";
import type { TurnAnalysis, AnalyzedError } from "../lib/api";

interface Props {
  userText: string;
  analysis: TurnAnalysis;
}

function typeColor(type: AnalyzedError["type"]): string {
  switch (type) {
    case "grammar":
    case "tense":
    case "word_order":
      return "bg-rose-500/15 text-rose-200 border-rose-500/40";
    case "preposition":
    case "article":
      return "bg-amber-500/15 text-amber-200 border-amber-500/40";
    case "vocab":
    case "spelling":
      return "bg-sky-500/15 text-sky-200 border-sky-500/40";
    case "pronunciation":
      return "bg-fuchsia-500/15 text-fuchsia-200 border-fuchsia-500/40";
    default:
      return "bg-zinc-500/15 text-zinc-200 border-zinc-500/40";
  }
}

function highlightSpans(text: string, errors: AnalyzedError[]): React.ReactNode[] {
  if (errors.length === 0) return [text];

  const ranges: Array<{ start: number; end: number; err: AnalyzedError }> = [];
  for (const err of errors) {
    const idx = text.toLowerCase().indexOf(err.span.toLowerCase());
    if (idx >= 0) ranges.push({ start: idx, end: idx + err.span.length, err });
  }
  ranges.sort((a, b) => a.start - b.start);

  const out: React.ReactNode[] = [];
  let cursor = 0;
  let key = 0;
  for (const r of ranges) {
    if (r.start < cursor) continue;
    if (r.start > cursor) out.push(<span key={key++}>{text.slice(cursor, r.start)}</span>);
    out.push(
      <span
        key={key++}
        className="underline decoration-rose-400 decoration-wavy decoration-2 underline-offset-4"
        title={r.err.explanation}
      >
        {text.slice(r.start, r.end)}
      </span>,
    );
    cursor = r.end;
  }
  if (cursor < text.length) out.push(<span key={key++}>{text.slice(cursor)}</span>);
  return out;
}

export function CorrectionBubble({ userText, analysis }: Props): JSX.Element | null {
  const [open, setOpen] = useState(false);

  if (analysis.errors.length === 0) {
    return (
      <div className="mt-1 inline-flex items-center gap-1 text-xs text-emerald-300/80">
        <span aria-hidden>{"✓"}</span>
        <span>Looks correct</span>
        <span className="text-zinc-500">{"·"}</span>
        <span className="text-zinc-400">level {analysis.estimated_level}</span>
      </div>
    );
  }

  const count = analysis.errors.length;

  return (
    <div className="mt-1">
      <div className="text-sm leading-relaxed text-zinc-200">
        {highlightSpans(userText, analysis.errors)}
      </div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="mt-1 inline-flex items-center gap-1 rounded-full border border-rose-500/40 bg-rose-500/10 px-2 py-0.5 text-xs text-rose-200 hover:bg-rose-500/20"
      >
        <span aria-hidden>{"✏️"}</span>
        <span>
          {count} {count === 1 ? "correction" : "corrections"}
        </span>
        <span className="text-zinc-400">{open ? "▴" : "▾"}</span>
      </button>
      {open && (
        <div className="mt-2 space-y-2 rounded-lg border border-zinc-700/60 bg-zinc-900/60 p-3 text-xs">
          <div>
            <div className="text-zinc-400">Suggested:</div>
            <div className="text-zinc-100">{analysis.corrected}</div>
          </div>
          <ul className="space-y-2">
            {analysis.errors.map((e, i) => (
              <li
                key={i}
                className={`rounded border px-2 py-1.5 ${typeColor(e.type)}`}
              >
                <div className="font-mono text-[11px] opacity-70">
                  {e.type} {"·"} {e.category}
                </div>
                <div className="font-medium">
                  {"“"}
                  {e.span}
                  {"”"}
                </div>
                <div className="mt-0.5 text-zinc-200/90">{e.explanation}</div>
              </li>
            ))}
          </ul>
          <div className="text-[11px] text-zinc-500">
            level {analysis.estimated_level} {"·"} topic: {analysis.topic}
          </div>
        </div>
      )}
    </div>
  );
}
